import type * as React from 'react';

import { cn } from '../../lib/utils';

/** Clamp a numeric value into the inclusive range [min, max]. Non-finite inputs collapse to `min`. */
function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

const DEFAULT_SIZE = 100;
const DEFAULT_STROKE_WIDTH = 8;
const DEFAULT_PRIMARY_COLOR = '#FF5722'; // dawn-orange
const DEFAULT_TRACK_COLOR = '#D9D9D9'; // grey-200

export interface DonutProps {
  /** 0-100. Values outside the range clamp; NaN / Infinity collapse to 0. */
  percent: number;
  /** Square pixel size of the SVG. Defaults to 100. */
  size?: number;
  /** Stroke width of both the track and the arc. Defaults to 8. */
  strokeWidth?: number;
  /** Foreground arc color. Defaults to dawn-orange (#FF5722). */
  primaryColor?: string;
  /** Background track color. Defaults to grey-200 (#D9D9D9). */
  trackColor?: string;
  /**
   * Text under the percent. Rendered capitalized in Outfit 10px. Newline
   * characters (`\n`) split the label into separate visual lines (Figma uses
   * `Data` / `Access` on two lines for the identity-access widgets).
   */
  centerLabel?: string;
  /**
   * Phase 5 addition. Optional secondary line under the percent — rendered
   * un-capitalized at Outfit 10px. Use this when the donut needs a stacked
   * "value + caption" center (e.g., `42%` / `of identities`). Keeps the
   * legacy `centerLabel` slot (capitalized two-line label) untouched — the
   * two slots can coexist but typically only one is used at a time.
   */
  caption?: string;
  /** Extra classes merged onto the root wrapper. */
  className?: string;
}

/**
 * Purely presentational donut chart. Renders a background track circle and a
 * foreground arc via stroke-dasharray/offset. No animation, no formatting —
 * the caller supplies a 0–100 `percent` and the component clamps + draws.
 *
 * Layout: a square flex container of size × size; percent + optional centerLabel
 * are stacked above the SVG inside the same square via absolute positioning.
 */
export const Donut: React.FC<DonutProps> = ({
  percent,
  size = DEFAULT_SIZE,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  primaryColor = DEFAULT_PRIMARY_COLOR,
  trackColor = DEFAULT_TRACK_COLOR,
  centerLabel,
  caption,
  className,
}) => {
  const clampedPercent = clamp(percent, 0, 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference - (clampedPercent / 100) * circumference;

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        data-testid="donut-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 -rotate-90"
        aria-hidden="true"
      >
        <circle
          data-testid="donut-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <circle
          data-testid="donut-arc"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={primaryColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="relative flex flex-col items-center justify-center leading-none">
        <span className="font-outfit text-[24px] font-medium leading-[1.2] text-dawn-orange">
          {clampedPercent}%
        </span>
        {caption ? (
          <span className="mt-1 text-center font-outfit text-[10px] leading-[1.2] text-dawn-black">
            {caption}
          </span>
        ) : null}
        {centerLabel ? (
          <span className="mt-1 whitespace-pre-line text-center font-outfit text-[10px] leading-[1.2] capitalize text-dawn-black">
            {centerLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};

Donut.displayName = 'Donut';
